<template>
  <v-container fluid>
    <v-row>
      <!-- ============== (A) 是否排除周日 / 周六+周日 ============== -->
      <v-col cols="12" class="mb-4">
        <v-select
          v-model="excludeSaturdays"
          :items="weekendExcludeOptions"
          label="稼働率計算設定"
          item-text="text"
          item-value="value"
          outlined
        ></v-select>
      </v-col>

      <!-- (原有) 用来失焦的隐藏 ref -->
      <div
        ref="focusTrap"
        tabindex="-1"
        style="opacity: 0; height: 0; width: 0; overflow: hidden;"
      ></div>

      <!-- ============== 1. 楼层图表 ============== -->
      <v-col cols="12">
        <v-card>
          <v-card-title>
            直近10日間各フロアの稼働率と通信機数
            <!-- 楼层图表: 问号图标 -->
            <v-spacer></v-spacer>
            <v-btn icon @click="dialogFloor = true">
              <v-icon color="primary">mdi-help-circle</v-icon>
            </v-btn>
          </v-card-title>

          <v-card-text>
            <VueApexCharts
              type="bar"
              :options="floorUtilizationChartOptions"
              :series="floorUtilizationSeries"
            />
          </v-card-text>
        </v-card>
      </v-col>

      <!-- (1) 楼层图表对话框 -->
      <v-dialog v-model="dialogFloor" max-width="600px">
        <v-card>
          <v-card-title class="text-h6">
            各フロアの稼働率と通信機数の計算方式
          </v-card-title>
          <v-card-text>
            <p>
              こちらのグラフでは、各フロアごとの「直近10日間の稼働率(%)」と
              「通信機数(台数)」をバー表示しています。<br /><br />
              稼働率(%)は以下の式により算出しています：<br />
              稼働率(%) = 実際稼働時間合計 ÷ (有効日数 × 8時間 × 台数) × 100
            </p>
            <p>
              土曜＆日曜を排除するオプションが有効の場合、土日分は
              分子・分母ともに含まず計算しています。
            </p>
          </v-card-text>
          <v-card-actions>
            <v-spacer></v-spacer>
            <v-btn color="primary" text @click="dialogFloor = false">
              閉じる
            </v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>

      <!-- ============== 2. 公司图表 ============== -->
      <v-col cols="12">
        <v-card>
          <v-card-title>
            直近10日間各社の稼働率と通信機数
            <!-- 公司图表: 问号图标 -->
            <v-spacer></v-spacer>
            <v-btn icon @click="dialogCompany = true">
              <v-icon color="primary">mdi-help-circle</v-icon>
            </v-btn>
          </v-card-title>

          <v-card-text>
            <VueApexCharts
              type="bar"
              :options="companyUtilizationChartOptions"
              :series="companyUtilizationSeries"
            />
          </v-card-text>
        </v-card>
      </v-col>

      <!-- (2) 公司图表对话框 -->
      <v-dialog v-model="dialogCompany" max-width="600px">
        <v-card>
          <v-card-title class="text-h6">
            各社の稼働率と通信機数の計算方式
          </v-card-title>
          <v-card-text>
            <p>
              こちらのグラフでは、各社ごとの「直近10日間の稼働率(%)」と
              「通信機数(台数)」をバー表示しています。<br /><br />
              計算方法はフロア別と同様で、同じく
              <strong>実際稼働時間 ÷ (有効日数 × 8時間 × 台数)</strong>
              で稼働率を求めています。
            </p>
          </v-card-text>
          <v-card-actions>
            <v-spacer></v-spacer>
            <v-btn color="primary" text @click="dialogCompany = false">
              閉じる
            </v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>

      <!-- ============== 3. 过去10天趋势图 ============== -->
      <v-col cols="12">
        <v-card>
          <v-card-title>
            直近10日間の全機械の稼働率推移
            <!-- 趋势图: 问号图标 -->
            <v-spacer></v-spacer>
            <v-btn icon @click="dialogTrend = true">
              <v-icon color="primary">mdi-help-circle</v-icon>
            </v-btn>
          </v-card-title>

          <v-card-text>
            <VueApexCharts
              type="bar"
              :options="utilizationTrendChartOptions"
              :series="utilizationTrendSeries"
            />
          </v-card-text>
        </v-card>
      </v-col>

      <!-- (3) 趋势图对话框 -->
      <v-dialog v-model="dialogTrend" max-width="600px">
        <v-card>
          <v-card-title class="text-h6">
            全機械の直近10日間稼働率推移の計算方式
          </v-card-title>
          <v-card-text>
            <p>
              全ての機械について、「過去10日間の日毎稼働率」を集計し、棒グラフで推移を確認できます。<br /><br />
              1日あたりの稼働率(%)は「その日の実際稼働時間合計」 ÷ 「(8時間 × 全機械台数)」で計算しています。
            </p>
          </v-card-text>
          <v-card-actions>
            <v-spacer></v-spacer>
            <v-btn color="primary" text @click="dialogTrend = false">
              閉じる
            </v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>

      <!-- ============== 4. 新增：周 / 月(9区间 + 翻页) ============== -->
      <v-col cols="12">
        <v-card>
          <v-card-title>
            <div class="d-flex align-center">
              <!-- (1) 选择周 / 月 -->
              <v-select
                v-model="viewMode"
                :items="viewModeOptions"
                label="選択範囲"
                item-text="text"
                item-value="value"
                outlined
                dense
                class="mr-2"
                style="max-width: 120px"
              ></v-select>

              <!-- (2) 左箭头: 往更早的 9 周 or 9 月 翻页 -->
              <v-btn icon @click="handlePrevRange">
                <v-icon>mdi-chevron-left</v-icon>
              </v-btn>

              <!-- (3) 右箭头: 往更近的 9 周 or 9 月 翻页 -->
              <!-- 当 pageIndex 为 0 时表示已到最新完整数据，禁用右箭头 -->
              <v-btn
                icon
                @click="handleNextRange"
                class="mr-4"
                :disabled="isNextDisabled"
              >
                <v-icon>mdi-chevron-right</v-icon>
              </v-btn>
            </div>
            <!-- 周 / 月图表: 问号图标 -->
            <v-spacer></v-spacer>
            <v-btn icon @click="dialogAggregated = true">
              <v-icon color="primary">mdi-help-circle</v-icon>
            </v-btn>
          </v-card-title>

          <v-card-text>
            <VueApexCharts
              type="bar"
              :options="aggregatedChartOptions"
              :series="aggregatedSeries"
            />
          </v-card-text>
        </v-card>
      </v-col>

      <!-- (4) 周 / 月图表对话框 -->
      <v-dialog v-model="dialogAggregated" max-width="600px">
        <v-card>
          <v-card-title class="text-h6">
            週 / 月 平均稼働率の計算方式
          </v-card-title>
          <v-card-text>
            <p>
              「週単位」または「月単位」で区切った各期間の平均稼働率を集計しています。<br /><br />
              1週（または1ヶ月）の稼働率は、期間内の実稼働時間合計を
              <strong>（期間内の有効日数 × 8時間 × 台数合計）</strong> で割って算出します。<br /><br />
              土日が除外される設定の場合は、週末分を含まず計算されます。
            </p>
          </v-card-text>
          <v-card-actions>
            <v-spacer></v-spacer>
            <v-btn color="primary" text @click="dialogAggregated = false">
              閉じる
            </v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>

    </v-row>
  </v-container>
</template>

<script>
import VueApexCharts from "vue-apexcharts";

export default {
  name: "FloorCompanyChart",
  components: {
    VueApexCharts,
  },
  props: {
    // (1) jsonData: 楼层图表、公司图表、过去10天趋势图 所需的数据结构
    //     每个楼层/公司下都有 data 数组，包含 { sigfox_msg_period, servertime, nine_days_ago..., today } 等字段
    jsonData: {
      type: Array,
      required: true,
    },
    // (2) dayDataString: 给“周 / 月”图表使用的每日数据 (示例: "2025/01/06:10:35;..."),
    //    => (日期:机器数:总使用小时)
    dayDataString: {
      type: String,
      required: true,
    },
  },
  data() {
    return {
      // ============== (A) 是否排除周六(加上周日) ==============
      excludeSaturdays: false,
      weekendExcludeOptions: [
        { value: false, text: "日曜非統計" }, // 仅排除周日
        { value: true, text: "土曜＆日曜非統計" }, // 排除周六+周日
      ],

      // ============== 1. 楼层图表 (原有) ==============
      floorUtilizationChartOptions: {
        chart: {
          id: "floor-utilization-rate",
        },
        xaxis: {
          categories: this.getUniqueFloors(),
        },
        
      },
      floorUtilizationSeries: [
        {
          name: "稼働率 (%)",
          data: this.calculateFloorUtilizationRates(),
        },
        {
          name: "通信機数",
          data: this.calculateFloorDeviceCounts(),
        },
      ],

      // ============== 2. 公司图表 (原有) ==============
      companyUtilizationChartOptions: {
        chart: {
          id: "company-utilization-rate",
        },
        xaxis: {
          categories: this.getUniqueCompanies(),
        },
        
      },
      companyUtilizationSeries: [
        {
          name: "稼働率 (%)",
          data: this.calculateCompanyUtilizationRates(),
        },
        {
          name: "通信機数",
          data: this.calculateCompanyDeviceCounts(),
        },
      ],

      // ============== 3. 10日趋势图表 (原有) ==============
      utilizationTrendChartOptions: {
        chart: {
          id: "utilization-trend",
        },
        xaxis: {
          categories: this.generateDateLabels(),
        },
        
      },
      utilizationTrendSeries: [
        {
          name: "稼働率 (%)",
          data: this.calculateUtilizationTrend(),
        },
      ],

      // ============== 4. 新增：周 / 月 (9区间 + 翻页) ==============
      viewMode: "week", // "week" or "month"
      viewModeOptions: [
        { value: "week", text: "週単位" },
        { value: "month", text: "月単位" },
      ],
      pageIndex: 0, // 翻页索引

      // 解析 dayDataString 后存储每日信息
      allDayData: [],

      // (4).1 图表配置
      aggregatedChartOptions: {
        chart: {
          id: "aggregated-usage-chart",
        },
        xaxis: {
          categories: [],
        },
        title: {
          text: "週 / 月 平均稼働率 ",
        },
        
      },
      aggregatedSeries: [
        {
          name: "稼働率 (%)",
          type: "bar",
          data: [],
        },
      ],

      // (4).2 平均值 & 中位值
      averageUsageRate: null,
      medianUsageRate: null,

      // ============== (B) 新增对话框控制变量 ==============
      dialogFloor: false,
      dialogCompany: false,
      dialogTrend: false,
      dialogAggregated: false,
    };
  },
  computed: {
    // 当 pageIndex 为 0 时，表示已到最新完整周期（本周或本月数据未结束，不作为有效数据），因此右侧翻页按钮禁用
    isNextDisabled() {
      return this.pageIndex <= 0;
    },
  },
  methods: {
    // ========================== (I) 楼层相关 ==========================
    getUniqueFloors() {
      return [...new Set(this.jsonData.map((entry) => entry.floor))];
    },
    calculateFloorUtilizationRates() {
      return this.getUniqueFloors().map((floor) =>
        this.calculateFloorUsageRate(floor)
      );
    },
    calculateFloorUsageRate(floor) {
      const totalHoursWorked = this.calculateTotalHoursByFloor(floor);
      const weekendDays = this.calculateWeekendDaysInPastTenDays();
      const effectiveDays = 10 - weekendDays;

      const floorData = this.jsonData.find((f) => f.floor === floor);
      // 过滤 period=1
      const machineCount = floorData
        ? floorData.data.filter((item) => item.sigfox_msg_period !== 1).length
        : 0;

      const totalAvailableHours = effectiveDays * 8 * machineCount;
      if (totalAvailableHours === 0) return 0;

      const usageRate = (totalHoursWorked / totalAvailableHours) * 100;
      return Math.round(usageRate);
    },
    calculateFloorDeviceCounts() {
      return this.getUniqueFloors().map((floor) =>
        this.calculateDeviceCountByFloor(floor)
      );
    },
    calculateDeviceCountByFloor(floor) {
      const floorData = this.jsonData.find((entry) => entry.floor === floor);
      if (!floorData) return 0;
      const validItems = floorData.data.filter(
        (item) => item.sigfox_msg_period !== 1
      );
      return validItems.length;
    },
    calculateTotalHoursByFloor(floor) {
      const floorData = this.jsonData.find((entry) => entry.floor === floor);
      if (!floorData) return 0;

      const validItems = floorData.data.filter(
        (item) => item.sigfox_msg_period !== 1
      );

      return validItems.reduce((total, item) => {
        const hoursArr = this.getValidWorkDays(item);
        return total + hoursArr.reduce((sum, h) => sum + h, 0);
      }, 0);
    },

    // ========================== (II) 公司相关 ==========================
    getUniqueCompanies() {
      const companies = [];
      this.jsonData.forEach((entry) => {
        entry.data
          .filter((item) => item.sigfox_msg_period !== 1)
          .forEach((item) => {
            if (!companies.includes(item.company)) {
              companies.push(item.company);
            }
          });
      });
      return companies;
    },
    calculateCompanyUtilizationRates() {
      return this.getUniqueCompanies().map((company) =>
        this.calculateCompanyUsageRate(company)
      );
    },
    calculateCompanyUsageRate(company) {
      const totalHoursWorked = this.calculateTotalHoursByCompany(company);
      const weekendDays = this.calculateWeekendDaysInPastTenDays();
      const effectiveDays = 10 - weekendDays;

      const machineCount = this.calculateCompanyMachineCount(company);
      const totalAvailableHours = effectiveDays * 8 * machineCount;
      if (totalAvailableHours === 0) return 0;

      const usageRate = (totalHoursWorked / totalAvailableHours) * 100;
      return Math.round(usageRate);
    },
    calculateCompanyDeviceCounts() {
      return this.getUniqueCompanies().map((company) =>
        this.calculateDeviceCountByCompany(company)
      );
    },
    calculateDeviceCountByCompany(company) {
      let deviceCount = 0;
      this.jsonData.forEach((entry) => {
        entry.data
          .filter((item) => item.sigfox_msg_period !== 1)
          .forEach((it) => {
            if (it.company === company) {
              deviceCount++;
            }
          });
      });
      return deviceCount;
    },
    calculateCompanyMachineCount(company) {
      let machineCount = 0;
      this.jsonData.forEach((entry) => {
        entry.data
          .filter((item) => item.sigfox_msg_period !== 1)
          .forEach((it) => {
            if (it.company === company) {
              machineCount++;
            }
          });
      });
      return machineCount;
    },
    calculateTotalHoursByCompany(company) {
      let totalHours = 0;
      this.jsonData.forEach((entry) => {
        entry.data
          .filter((item) => item.sigfox_msg_period !== 1)
          .forEach((it) => {
            if (it.company === company) {
              const hoursArr = this.getValidWorkDays(it);
              totalHours += hoursArr.reduce((sum, h) => sum + h, 0);
            }
          });
      });
      return totalHours;
    },

    // ========================== (III) 10日趋势图表 ==========================
    generateDateLabels() {
      const labels = [];
      const today = new Date();
      for (let i = 9; i >= 0; i--) {
        const pastDate = new Date(today);
        pastDate.setDate(today.getDate() - i);
        const fd = `${pastDate.getFullYear()}/${
          pastDate.getMonth() + 1
        }/${pastDate.getDate()}`;
        labels.push(fd);
      }
      return labels;
    },
    calculateWeekendDaysInPastTenDays() {
      let weekendCount = 0;
      const today = new Date();
      for (let i = 0; i < 10; i++) {
        const day = new Date(today);
        day.setDate(today.getDate() - i);

        if (day.getDay() === 0) {
          weekendCount++;
          continue;
        }
        if (this.excludeSaturdays && day.getDay() === 6) {
          weekendCount++;
        }
      }
      return weekendCount;
    },
    calculateUtilizationTrend() {
      const trendData = Array(10).fill(0);

      // 累计总使用小时
      this.jsonData.forEach((entry) => {
        const validItems = entry.data.filter(
          (item) => item.sigfox_msg_period !== 1
        );
        validItems.forEach((item) => {
          const hoursArr = this.getValidWorkDays(item);
          hoursArr.forEach((h, idx) => {
            trendData[idx] += h;
          });
        });
      });

      // 总机器数
      let totalMachines = 0;
      this.jsonData.forEach((entry) => {
        totalMachines += entry.data.filter(
          (item) => item.sigfox_msg_period !== 1
        ).length;
      });

      const totalAvailableHours = 8 * totalMachines;
      if (totalAvailableHours === 0) {
        return Array(10).fill(0);
      }

      return trendData.map((h) => Math.round((h / totalAvailableHours) * 100));
    },

    /**
     * 提取设备在过去10天 (today, one_days_ago, ..., nine_days_ago) 的使用小时
     * 如果服务器时间与当前时间差>20小时, 视为无效 => 返回空[]
     */
    getValidWorkDays(item) {
      const now = new Date();
      const serverTime = new Date(item.servertime);
      // 若差>20h => 无效 (这里为了演示改成 500h 避免太严)
      if ((now - serverTime) / (1000 * 60 * 60) > 500) {
        return [];
      }

      // 对应 item 里“过去10天”字段的键名；下标 0 => 最早(9天前)，下标 9 => today
      const dayKeys = [
        "nine_days_ago",
        "eight_days_ago",
        "seven_days_ago",
        "six_days_ago",
        "five_days_ago",
        "four_days_ago",
        "three_days_ago",
        "two_days_ago",
        "one_days_ago",
        "today",
      ];

      // 准备一个数组来存放“修正后”的使用小时
      const usageArr = [];

      // 逐日检查
      for (let i = 0; i < 10; i++) {
        const usageKey = dayKeys[i];
        // 若缺失则默认为0
        let usage = item[usageKey] || 0;

        // 计算该天对应的实际日期
        const offset = 9 - i; // 9,8,...,0
        const dateObj = new Date(now);
        dateObj.setDate(dateObj.getDate() - offset);

        // 如果是周日 => 0，若 excludeSaturdays = true 并且是周六 => 0
        const dayOfWeek = dateObj.getDay(); // 0=周日,6=周六
        if (dayOfWeek === 0) {
          usage = 0;
        } else if (this.excludeSaturdays && dayOfWeek === 6) {
          usage = 0;
        }

        usageArr.push(usage);
      }

      return usageArr;
    },

    // ========================== (IV) 周 / 月 (9区间) ==========================
    // --- (A) 解析 dayDataString ---
    parseDayDataString() {
      const segments = this.dayDataString
        .split(";")
        .map((s) => s.trim())
        .filter((s) => s !== "");

      const result = [];
      segments.forEach((seg) => {
        const [dateStr, machineStr, usageStr] = seg.split(":");
        if (!dateStr || !machineStr || !usageStr) return;

        const mc = parseFloat(machineStr);
        const tu = parseFloat(usageStr);

        result.push({
          date: dateStr, // "YYYY/MM/DD"
          machineCount: mc, // 机器数量
          totalUsage: tu, // 当日总使用小时
        });
      });

      // 按日期升序
      result.sort((a, b) => new Date(a.date) - new Date(b.date));
      return result;
    },

    // --- (B) 周模式 ---
    getMondayOf(date) {
      const tmp = new Date(date);
      const day = tmp.getDay(); // 0=周日,1=周一,...,6=周六
      const distance = day === 0 ? 6 : day - 1;
      tmp.setDate(tmp.getDate() - distance);
      return tmp;
    },
    getWeekRange(mondayDate) {
      const start = new Date(mondayDate);
      const end = new Date(mondayDate);
      end.setDate(end.getDate() + 6); // 周日
      return { start, end };
    },

    // --- (C) 月模式 ---
    getFirstDayOfMonth(date) {
      return new Date(date.getFullYear(), date.getMonth(), 1);
    },
    getMonthRange(date) {
      const start = new Date(date.getFullYear(), date.getMonth(), 1);
      const end = new Date(date.getFullYear(), date.getMonth() + 1, 0);
      return { start, end };
    },

    // --- (D) 更新周 / 月 图表 ---
    updateAggregatedUsageChart() {
      if (this.viewMode === "week") {
        this.updateWeeklyChart();
      } else {
        this.updateMonthlyChart();
      }
    },

    updateWeeklyChart() {
      const now = new Date();
      let currentWeekMonday = this.getMondayOf(now);
      const currentWeekRange = this.getWeekRange(currentWeekMonday);
      // 如果当前周未结束，则使用上一个完整周作为参考（排除本周数据）
      if (currentWeekRange.end >= now) {
        currentWeekMonday.setDate(currentWeekMonday.getDate() - 7);
      }

      // 最左侧周一 => 往前 (8 + 9*pageIndex) 个周
      const startMonday = new Date(currentWeekMonday);
      startMonday.setDate(startMonday.getDate() - (8 + 9 * this.pageIndex) * 7);

      const weekLabels = [];
      const usageRateData = [];
      const machineData = [];

      for (let i = 0; i < 9; i++) {
        const monday = new Date(startMonday);
        monday.setDate(startMonday.getDate() + i * 7);
        const { start, end } = this.getWeekRange(monday);

        // X轴标签 => 周一日期
        const label = this.formatDate(monday);
        weekLabels.push(label);

        // 统计(排除周末 + 排除未来日期)后的 usage & machine
        const filteredDays = this.filterDaysInRange(start, end);
        const sumUsage = filteredDays.reduce((s, d) => s + d.usage, 0);
        const sumMachine = filteredDays.reduce((s, d) => s + d.machine, 0);
        const validDays = filteredDays.length;

        let avgMachineCount = 0;
        if (validDays > 0) {
          avgMachineCount = sumMachine / validDays;
        }

        let usageRate = null;
        if (sumMachine > 0) {
          usageRate = Math.round((sumUsage / (8 * sumMachine)) * 100);
        }

        usageRateData.push(usageRate);
        machineData.push(Math.round(avgMachineCount));
      }

      // 更新图表
      this.aggregatedChartOptions = {
        ...this.aggregatedChartOptions,
        xaxis: {
          ...this.aggregatedChartOptions.xaxis,
          categories: weekLabels,
        },
      };
      this.aggregatedSeries = [
        {
          name: "稼働率 (%)",
          type: "bar",
          data: usageRateData,
        },
      ];

      this.computeAverageAndMedian(usageRateData);
    },

    updateMonthlyChart() {
      const now = new Date();
      let currentMonthFirstDay = this.getFirstDayOfMonth(now);
      const currentMonthRange = this.getMonthRange(currentMonthFirstDay);
      // 如果当前月未结束，则使用上一个完整月作为参考（排除本月数据）
      if (currentMonthRange.end >= now) {
        currentMonthFirstDay = new Date(
          currentMonthFirstDay.getFullYear(),
          currentMonthFirstDay.getMonth() - 1,
          1
        );
      }

      // 最左侧月份 => 往前 (8 + 9*pageIndex) 个月
      const startMonth = new Date(
        currentMonthFirstDay.getFullYear(),
        currentMonthFirstDay.getMonth() - (8 + 9 * this.pageIndex),
        1
      );

      const monthLabels = [];
      const usageRateData = [];
      const machineData = [];

      for (let i = 0; i < 9; i++) {
        const monthDate = new Date(
          startMonth.getFullYear(),
          startMonth.getMonth() + i,
          1
        );
        const { start, end } = this.getMonthRange(monthDate);

        // X轴 => "YYYY/MM"
        const y = monthDate.getFullYear();
        const m = String(monthDate.getMonth() + 1).padStart(2, "0");
        const label = `${y}/${m}`;
        monthLabels.push(label);

        // 统计(排除周末 + 排除未来日期)
        const filteredDays = this.filterDaysInRange(start, end);
        const sumUsage = filteredDays.reduce((s, d) => s + d.usage, 0);
        const sumMachine = filteredDays.reduce((s, d) => s + d.machine, 0);
        const validDays = filteredDays.length;

        let avgMachineCount = 0;
        if (validDays > 0) {
          avgMachineCount = sumMachine / validDays;
        }

        let usageRate = null;
        if (sumMachine > 0) {
          usageRate = Math.round((sumUsage / (8 * sumMachine)) * 100);
        }

        usageRateData.push(usageRate);
        machineData.push(Math.round(avgMachineCount));
      }

      // 更新图表
      this.aggregatedChartOptions = {
        ...this.aggregatedChartOptions,
        xaxis: {
          ...this.aggregatedChartOptions.xaxis,
          categories: monthLabels,
        },
      };
      this.aggregatedSeries = [
        {
          name: "稼働率 (%)",
          type: "bar",
          data: usageRateData,
        },
      ];

      this.computeAverageAndMedian(usageRateData);
    },

    /**
     * 在区间 [rangeStart, rangeEnd] 中，按天遍历:
     * 1) 若 day > 今天 => break (不再统计未来天数)
     * 2) 若是周日 => 跳过
     * 3) 若 excludeSaturdays=true & 周六 => 跳过
     * 4) 否则在 allDayData 中找到 usage & machine
     */
    filterDaysInRange(rangeStart, rangeEnd) {
      const result = [];
      const cur = new Date(rangeStart);
      const now = new Date(); // 今天

      while (cur <= rangeEnd) {
        if (cur > now) {
          // 未来日期 => 不统计后面
          break;
        }
        const dow = cur.getDay(); // 0=周日,6=周六
        if (dow === 0) {
          cur.setDate(cur.getDate() + 1);
          continue;
        }
        if (this.excludeSaturdays && dow === 6) {
          cur.setDate(cur.getDate() + 1);
          continue;
        }

        const dateStr = this.formatDate(cur);
        const found = this.allDayData.find((d) => d.date === dateStr);
        if (found) {
          result.push({
            usage: found.totalUsage,
            machine: found.machineCount,
          });
        } else {
          // 若没有数据 => usage=0, machine=0
          result.push({ usage: 0, machine: 0 });
        }

        cur.setDate(cur.getDate() + 1);
      }
      return result;
    },

    computeAverageAndMedian(usageRateData) {
      const validRates = usageRateData.filter((r) => r !== null);
      if (validRates.length === 0) {
        this.averageUsageRate = null;
        this.medianUsageRate = null;
        return;
      }
      const sum = validRates.reduce((acc, val) => acc + val, 0);
      this.averageUsageRate = Math.round(sum / validRates.length);

      const sorted = [...validRates].sort((a, b) => a - b);
      const mid = Math.floor(sorted.length / 2);
      if (sorted.length % 2 === 0) {
        this.medianUsageRate = Math.round((sorted[mid - 1] + sorted[mid]) / 2);
      } else {
        this.medianUsageRate = sorted[mid];
      }
    },

    formatDate(dateObj) {
      const y = dateObj.getFullYear();
      const m = String(dateObj.getMonth() + 1).padStart(2, "0");
      const d = String(dateObj.getDate()).padStart(2, "0");
      return `${y}/${m}/${d}`;
    },

    // 翻页：向更早的区间
    handlePrevRange() {
      // 更早 => pageIndex++
      this.pageIndex++;
      this.updateAggregatedUsageChart();
    },
    // 翻页：向更近的区间
    handleNextRange() {
      // 如果 pageIndex 为 0，表示已到最新完整周期，不允许向右移动
      if (this.pageIndex <= 0) return;
      this.pageIndex--;
      this.updateAggregatedUsageChart();
    },
  },

  watch: {
    // 切换“排除周六” => 重新刷新所有图表
    excludeSaturdays() {
      // 1) 楼层图表
      this.floorUtilizationSeries = [
        {
          name: "稼働率 (%)",
          data: this.calculateFloorUtilizationRates(),
        },
        {
          name: "通信機数",
          data: this.calculateFloorDeviceCounts(),
        },
      ];
      // 2) 公司图表
      this.companyUtilizationSeries = [
        {
          name: "稼働率 (%)",
          data: this.calculateCompanyUtilizationRates(),
        },
        {
          name: "通信機数",
          data: this.calculateCompanyDeviceCounts(),
        },
      ];
      // 3) 10日趋势图
      this.utilizationTrendSeries = [
        {
          name: "稼働率 (%)",
          data: this.calculateUtilizationTrend(),
        },
      ];
      // 4) 周 / 月 图表
      this.updateAggregatedUsageChart();
    },

    // dayDataString 改变 => 重新解析 & 更新周/月图表
    dayDataString() {
      this.allDayData = this.parseDayDataString();
      this.updateAggregatedUsageChart();
    },

    // 选择“周 / 月” => 重置 pageIndex=0 => 更新图表
    viewMode() {
      this.pageIndex = 0;
      this.updateAggregatedUsageChart();
    },
  },

  mounted() {
    // (1) 初次解析
    this.allDayData = this.parseDayDataString();
    // (2) 更新 周/月 图表
    this.updateAggregatedUsageChart();

    // (3) 原有的“延时点击 body 失焦”逻辑
    setTimeout(() => {
      const eventOptions = {
        view: window,
        bubbles: true,
        cancelable: true,
        clientX: 0,
        clientY: 0,
      };
      const mouseDownEvent = new MouseEvent("mousedown", eventOptions);
      const mouseUpEvent = new MouseEvent("mouseup", eventOptions);
      const clickEvent = new MouseEvent("click", eventOptions);

      document.body.dispatchEvent(mouseDownEvent);
      document.body.dispatchEvent(mouseUpEvent);
      document.body.dispatchEvent(clickEvent);
    }, 500);
  },
};
</script>

<style scoped>
/* 用于平均和中位稼働率显示的简单样式 */
.d-flex.flex-column span {
  font-size: 14px;
  margin-bottom: 4px;
}
.d-flex.flex-column strong {
  color: #1976D2; /* Vuetify 主色，可自行调整 */
}
</style>
